/**
 * «Что ждут от хозяйства» глазами диспетчера: выбор хозяйства и список
 * «отстают сильнее всех» (решение №1873).
 *
 * У роли с районной областью (менеджер, хоким) экрану «Что от меня ждут» не от
 * чьего лица смотреть: хозяйство не названо. Раньше здесь был тупик «хозяйство
 * не выбрано»; теперь — поиск хозяйства, который пишет `actor_id` в адрес, и
 * восемь хозяйств, где больше всего гектаров работ без отчёта «выполнено» (та же
 * величина, что порядок по умолчанию в реестре «Хозяйства → Сезон»). Каждое
 * ведёт к работам хозяйства, а «Внести за хозяйство» — на «Ввод за хозяйство».
 *
 * В меню экран не добавляется: сюда ведут «Хозяйства», «Кампании», «Проверка».
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Building2, ClipboardList, ListChecks, PencilLine } from 'lucide-react';
import { api } from '../../api/client';
import { useRequest } from '../../api/useRequest';
import { useAnalysisContext } from '../../analysis/context';
import { runtimeLocale } from '../../i18n/runtime';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { sortFarms, type FarmMetrics } from '../../astra/data-source/api/farmBench';
import { useDistrictFarms } from '../../astra/pages/farmBenchParts';
import { usePageTrail } from '../../astra/shell/ShellContext';
import { ha, num, pct } from '../../astra/lib/format';
import { Badge } from '../../astra/ui/Badge';
import { Card } from '../../astra/ui/Card';
import { SearchChoice, type Choice } from '../../astra/ui/AnalysisControls';
import { EmptyState } from '../../astra/ui/EmptyState';
import { KpiCard } from '../../astra/ui/KpiCard';
import { LoadError } from '../../astra/ui/LoadError';
import { PageHeader } from '../../astra/ui/PageHeader';
import { Skeleton, SkeletonList } from '../../astra/ui/Skeleton';
import './farmChooser.css';

/** «Ввод за хозяйство»: хозяйство передаётся через `actor_id` (экран читает его и выбирает заранее). */
const ENTRY_PATH = "/ops/entry";

const m = defineMessages({
  trailDistrict: { id: 'canonWorks.my.dispatcher.trail.district', defaultMessage: 'Район', description: 'Хлебная крошка: уровень «Район» на экране «Что ждут от хозяйства» (диспетчер)' },
  titleChoose: { id: 'canonWorks.my.dispatcher.title', defaultMessage: 'Что ждут от хозяйства', description: 'Заголовок экрана для диспетчера (менеджер, хоким), пока хозяйство не выбрано' },
  purposeChoose: { id: 'canonWorks.my.dispatcher.purpose', defaultMessage: 'Выберите хозяйство, чтобы увидеть работы, которых от него ждут, и внести отчёт вместо него.', description: 'Подзаголовок экрана для диспетчера, пока хозяйство не выбрано' },
  purposeFarm: { id: 'canonWorks.my.dispatcher.purposeFarm', defaultMessage: 'Так этот экран выглядит у самого хозяйства. Отчёт, внесённый отсюда, записывается «от имени хозяйства» и помечается для всех, кто его читает.', description: 'Подзаголовок экрана диспетчера, когда выбрано хозяйство: что он видит и как помечаются отчёты' },
  trailFarm: { id: 'canonWorks.my.dispatcher.trail.farm', defaultMessage: 'Хозяйство', description: 'Хлебная крошка: уровень «Хозяйство» на экране диспетчера с выбранным хозяйством' },
  farmTitle: { id: 'canonWorks.my.dispatcher.farmTitle', defaultMessage: 'Хозяйство {farm}', description: 'Заголовок экрана диспетчера с выбранным хозяйством; {farm} — название хозяйства' },
  viewAs: { id: 'canonWorks.my.dispatcher.viewAs', defaultMessage: 'вы смотрите как диспетчер', description: 'Метка в шапке экрана: пользователь с районной областью смотрит глазами хозяйства; строчными' },
  farmControl: { id: 'canonWorks.my.dispatcher.control', defaultMessage: 'Выберите хозяйство', description: 'Подпись поля поиска хозяйства в шапке экрана диспетчера' },
  farmControlChange: { id: 'canonWorks.my.dispatcher.controlChange', defaultMessage: 'Хозяйство', description: 'Подпись поля поиска хозяйства в шапке экрана диспетчера, когда хозяйство уже выбрано; коротко' },
  enter: { id: 'canonWorks.my.dispatcher.enter', defaultMessage: 'Внести за хозяйство', description: 'Кнопка: перейти на экран «Ввод за хозяйство» с выбранным хозяйством' },
  farmsFailed: { id: 'canonWorks.my.dispatcher.farmsFailed', defaultMessage: 'Реестр хозяйств не загрузился — выберите хозяйство из списка ниже.', description: 'Подсказка под шапкой, когда список хозяйств для поиска не загрузился' },
  kpiFarmsLabel: { id: 'canonWorks.my.dispatcher.kpi.farms.label', defaultMessage: 'Хозяйств в агроцикле', description: 'Подпись KPI: сколько хозяйств с работами на полях в агроцикле; коротко' },
  kpiFarmsNote: { id: 'canonWorks.my.dispatcher.kpi.farms.note', defaultMessage: 'агроцикл {cycle}', description: 'Нижняя строка KPI «Хозяйств в агроцикле»; {cycle} — название агроцикла («Хлопок-2026»)' },
  kpiBehindLabel: { id: 'canonWorks.my.dispatcher.kpi.behind.label', defaultMessage: 'После срока', description: 'Подпись KPI: у скольких хозяйств есть работа после срока без отчёта; коротко' },
  kpiBehindUnit: { id: 'canonWorks.my.dispatcher.kpi.behind.unit', defaultMessage: '{count, plural, one {хозяйство} few {хозяйства} many {хозяйств} other {хозяйства}}', description: 'Единица рядом с числом в KPI «После срока»; без числа, склоняется по {count}' },
  kpiBehindNote: { id: 'canonWorks.my.dispatcher.kpi.behind.note', defaultMessage: 'из {total}: окно работы закрылось, отчёта нет', description: 'Нижняя строка KPI «После срока»; {total} — всего хозяйств в агроцикле числом' },
  kpiUndoneLabel: { id: 'canonWorks.my.dispatcher.kpi.undone.label', defaultMessage: 'Не выполнено', description: 'Подпись KPI: сколько гектаров работ, срок которых наступил, без отчёта «выполнено»; коротко' },
  kpiUndoneUnit: { id: 'canonWorks.my.dispatcher.kpi.undone.unit', defaultMessage: 'га работ', description: 'Единица рядом с числом в KPI «Не выполнено»: гектары работ (сумма площадей работ, не земля); коротко' },
  kpiUndoneNote: { id: 'canonWorks.my.dispatcher.kpi.undone.note', defaultMessage: 'сумма по хозяйствам агроцикла', description: 'Нижняя строка KPI «Не выполнено»' },
  listTitle: { id: 'canonWorks.my.dispatcher.list.title', defaultMessage: 'Отстают сильнее всех', description: 'Заголовок карточки со списком хозяйств, у которых больше всего гектаров работ без отчёта' },
  listAside: { id: 'canonWorks.my.dispatcher.list.aside', defaultMessage: '{cycle} · по гектарам работ без отчёта', description: 'Справа в заголовке списка «Отстают сильнее всех»; {cycle} — название агроцикла; строчными' },
  cardSub: { id: 'canonWorks.my.dispatcher.card.sub', defaultMessage: '{massif} · {land} га земли', description: 'Мелкая строка карточки хозяйства; {massif} — название массива, {land} — гектары земли числом' },
  cardSubNoMassif: { id: 'canonWorks.my.dispatcher.card.subNoMassif', defaultMessage: '{land} га земли', description: 'Мелкая строка карточки хозяйства без массива; {land} — гектары земли числом' },
  cardFacts: { id: 'canonWorks.my.dispatcher.card.facts', defaultMessage: 'не выполнено {undone} га работ · выполнено {done}', description: 'Строка фактов карточки хозяйства; {undone} — гектары работ без отчёта числом, {done} — доля выполненного по площади работ с «%»' },
  cardFactsNoShare: { id: 'canonWorks.my.dispatcher.card.factsNoShare', defaultMessage: 'не выполнено {undone} га работ', description: 'Строка фактов карточки хозяйства, когда доли выполненного нет; {undone} — гектары работ числом' },
  cardOpen: { id: 'canonWorks.my.dispatcher.card.open', defaultMessage: 'Открыть работы хозяйства {farm}', description: 'Доступное имя нажимаемой карточки хозяйства; {farm} — название' },
  doneBar: { id: 'canonWorks.my.dispatcher.card.doneBar', defaultMessage: 'Выполнено {pct} площади работ', description: 'Доступное имя полоски выполнения в карточке хозяйства; {pct} — доля с «%»' },
  emptyTitle: { id: 'canonWorks.my.dispatcher.empty.title', defaultMessage: 'Отстающих хозяйств нет', description: 'Заголовок пустого состояния списка «Отстают сильнее всех»' },
  emptyReason: { id: 'canonWorks.my.dispatcher.empty.reason', defaultMessage: 'на дату среза ни у одного хозяйства агроцикла нет работ, срок которых наступил без отчёта «выполнено». Хозяйство можно выбрать поиском выше.', description: 'Пояснение пустого состояния списка «Отстают сильнее всех»' },
  noCycleTitle: { id: 'canonWorks.my.dispatcher.noCycle.title', defaultMessage: 'Нет агроцикла для списка', description: 'Заголовок пустого состояния: в срезе нет агроцикла, по которому можно найти отстающих' },
  noCycleReason: { id: 'canonWorks.my.dispatcher.noCycle.reason', defaultMessage: 'в срезе нет ни одного агроцикла. Хозяйство можно выбрать поиском выше.', description: 'Пояснение пустого состояния: нет агроцикла' },
  loadWhat: { id: 'canonWorks.my.dispatcher.loadWhat', defaultMessage: 'хозяйства агроцикла', description: 'Что не загрузилось (винительный падеж) в «Не удалось загрузить …»: картина хозяйств агроцикла' },
  loadingFarms: { id: 'canonWorks.my.dispatcher.loading', defaultMessage: 'хозяйства агроцикла', description: 'Что загружается: подпись заглушки списка отстающих хозяйств для чтеца с экрана; строчными' },
  farmFallback: { id: 'canonWorks.my.dispatcher.farmFallback', defaultMessage: 'хозяйство #{id}', description: 'Подпись хозяйства без названия в списке поиска; {id} — номер' },
});

const PAGE = 500;
const SHOWN = 8;

/**
 * Все хозяйства района для поиска: реестр отдаёт не больше 500 строк за запрос,
 * поэтому страницы читаются подряд. Падение запроса не роняет экран — поиск
 * просто пуст, а список «отстают» работает отдельно.
 */
export function useFarmOptions(enabled: boolean): { options: Choice[]; failed: boolean } {
  const t = useT();
  const context = useAnalysisContext();
  const req = useRequest(async () => {
    // Своё хозяйство у фермера уже выбрано областью: реестр ему не нужен и не читается.
    if (!enabled) return [];
    const first = await api.actors({ ...context.params, limit: PAGE, offset: 0 });
    const total = first.page.total ?? first.items.length;
    const rest: Promise<typeof first>[] = [];
    for (let offset = PAGE; offset < total; offset += PAGE) rest.push(api.actors({ ...context.params, limit: PAGE, offset }));
    const more = await Promise.all(rest);
    return [first, ...more].flatMap((page) => page.items);
  }, [context.key, enabled]);
  const options = useMemo(
    () =>
      (req.data ?? [])
        .map((row) => ({ id: String(row.id), name: row.name ?? t(m.farmFallback, { id: row.id }) }))
        .sort((a, b) => a.name.localeCompare(b.name, runtimeLocale())),
    [req.data, t],
  );
  return { options, failed: req.state === 'error' && !req.data };
}

export interface DispatcherHeaderProps {
  /** Выбранное хозяйство; `null` — ещё не выбрано. */
  farm: { id: string; name: string } | null;
  options: Choice[];
  optionsFailed: boolean;
  /** Есть ли у роли право вносить отчёт за хозяйство (`can_report`). */
  canEnter: boolean;
  /** Выбрали хозяйство (`null` — снять выбор). */
  onPick: (id: string | null) => void;
}

/** Шапка экрана диспетчера: хозяйство, поиск другого и «Внести за хозяйство». */
export function DispatcherHeader({ farm, options, optionsFailed, canEnter, onPick }: DispatcherHeaderProps) {
  const t = useT();
  const context = useAnalysisContext();
  usePageTrail(farm
    ? [{ level: t(m.trailDistrict), name: t(m.titleChoose), to: `/my-works${context.linkSearch()}` }, { level: t(m.trailFarm), name: farm.name }]
    : [{ level: t(m.trailDistrict), name: t(m.titleChoose) }]);
  return (
    <PageHeader
      level={farm ? 'farm' : 'district'}
      title={farm ? t(m.farmTitle, { farm: farm.name }) : t(m.titleChoose)}
      purpose={farm ? t(m.purposeFarm) : t(m.purposeChoose)}
      actions={(
        <div className="mw-controls">
          <SearchChoice
            label={farm ? t(m.farmControlChange) : t(m.farmControl)}
            value={farm?.id ?? ''}
            options={options}
            onChange={(id) => { if (id && id !== farm?.id) onPick(id); }}
          />
          {farm && canEnter ? (
            <Link className="astra-button astra-button-primary" to={{ pathname: ENTRY_PATH, search: context.linkSearch({ actor_id: farm.id }) }}>
              <PencilLine size={15} /> {t(m.enter)}
            </Link>
          ) : null}
        </div>
      )}
    >
      {farm ? <div><Badge tone="copper" icon={Building2}>{t(m.viewAs)}</Badge></div> : null}
      {optionsFailed ? <p className="mw-note" role="status">{t(m.farmsFailed)}</p> : null}
    </PageHeader>
  );
}

/** Хозяйства по убыванию гектаров работ без отчёта; без таких гектаров в список не попадают. */
export function laggards(metrics: readonly FarmMetrics[], limit = SHOWN): FarmMetrics[] {
  return sortFarms(metrics.filter((row) => (row.undoneHa ?? 0) > 0), 'undoneHa', 'desc').slice(0, limit);
}

function FarmCard({ row, canEnter, onPick }: { row: FarmMetrics; canEnter: boolean; onPick: (id: string) => void }) {
  const t = useT();
  const context = useAnalysisContext();
  const done = row.doneAreaPct;
  return (
    <li className="mw-lag">
      <button type="button" className="mw-lag__main" onClick={() => onPick(String(row.id))} aria-label={t(m.cardOpen, { farm: row.name })}>
        <span className="mw-lag__name">{row.name}</span>
        <span className="mw-lag__sub">
          {row.massifName && row.areaHa !== null ? t(m.cardSub, { massif: row.massifName, land: num(row.areaHa) }) : row.areaHa !== null ? t(m.cardSubNoMassif, { land: num(row.areaHa) }) : row.massifName ?? ''}
        </span>
        {done !== null ? (
          <span className="mw-lag__bar" role="img" aria-label={t(m.doneBar, { pct: pct(done, 1) })}><i style={{ width: `${Math.max(0, Math.min(100, done))}%` }} /></span>
        ) : null}
        <span className="mw-lag__facts">
          {done !== null ? t(m.cardFacts, { undone: ha(row.undoneHa), done: pct(done, 1) }) : t(m.cardFactsNoShare, { undone: ha(row.undoneHa) })}
        </span>
      </button>
      {canEnter ? (
        <Link className="mw-lag__enter" to={{ pathname: ENTRY_PATH, search: context.linkSearch({ actor_id: row.id }) }}>
          {t(m.enter)} <ArrowUpRight size={14} />
        </Link>
      ) : null}
    </li>
  );
}

/** Вместо тупика «хозяйство не выбрано»: три показателя и восемь отстающих хозяйств. */
export function FarmChooser({ canEnter, onPick }: { canEnter: boolean; onPick: (id: string) => void }) {
  const t = useT();
  const farms = useDistrictFarms();
  const top = useMemo(() => laggards(farms.metrics), [farms.metrics]);
  // «После срока» — земля с работой, окно которой закрылось без отчёта; «не выполнено» шире: срок наступил.
  const behind = useMemo(() => farms.metrics.filter((row) => (row.overdueHa ?? 0) > 0), [farms.metrics]);
  const undoneTotal = useMemo(() => farms.metrics.reduce((sum, row) => sum + (row.undoneHa ?? 0), 0), [farms.metrics]);

  if (farms.error && !farms.picture.data) {
    return <LoadError what={t(m.loadWhat)} error={farms.error} onRetry={() => { farms.cycles.reload(); farms.picture.reload(); }} />;
  }
  if (farms.loading) {
    return (
      <div className="mw-chooser" aria-busy="true">
        <div className="mw-kpis">{Array.from({ length: 3 }, (_, i) => <Skeleton key={i} variant="block" width="100%" height={132} />)}</div>
        <Card title={t(m.listTitle)}><SkeletonList label={t(m.loadingFarms)} quiet items={4} tone="soft" /></Card>
      </div>
    );
  }
  if (!farms.cycle) {
    return <Card title={t(m.listTitle)}><EmptyState title={t(m.noCycleTitle)} reason={t(m.noCycleReason)} /></Card>;
  }
  const cycleName = farms.cycleName ?? '';
  return (
    <div className="mw-chooser">
      <div className="mw-kpis">
        <KpiCard icon={Building2} label={t(m.kpiFarmsLabel)} value={num(farms.metrics.length)} note={t(m.kpiFarmsNote, { cycle: cycleName })} />
        <KpiCard
          icon={ClipboardList}
          label={t(m.kpiBehindLabel)}
          value={num(behind.length)}
          unit={t(m.kpiBehindUnit, { count: behind.length })}
          tone={behind.length > 0 ? 'warning' : undefined}
          note={t(m.kpiBehindNote, { total: num(farms.metrics.length) })}
        />
        <KpiCard icon={ListChecks} label={t(m.kpiUndoneLabel)} value={num(Math.round(undoneTotal))} unit={t(m.kpiUndoneUnit)} note={t(m.kpiUndoneNote)} />
      </div>
      <Card title={t(m.listTitle)} aside={t(m.listAside, { cycle: cycleName })}>
        {top.length ? (
          <ul className="mw-laglist">
            {top.map((row) => <FarmCard key={row.id} row={row} canEnter={canEnter} onPick={onPick} />)}
          </ul>
        ) : (
          <EmptyState compact title={t(m.emptyTitle)} reason={t(m.emptyReason)} />
        )}
      </Card>
    </div>
  );
}
